import { useEffect, useState, useCallback } from "react";
import { Plus, Trash, PencilSimple, UserCircle, Pause, Play, ChatText, PaperPlaneTilt, PushPin, ShieldStar, Vault } from "@phosphor-icons/react";
import { BarChart, Bar, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { name: "", email: "", password: "", kasa_account_id: "", permissions: { transactions: true, payments: true, contacts: true, admin: false } };
const PERM_LABELS = { transactions: "Gelir/Gider", payments: "Ödeme/Tahsilat", contacts: "Cari Ekleme" };
const NONE = "__none__";

export default function Staff() {
  const [items, setItems] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activity, setActivity] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get("/staff").then((r) => setItems(r.data)).catch(() => {});
    api.get("/messages").then((r) => setMessages(r.data)).catch(() => {});
    api.get("/accounts").then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/staff/activity").then((r) => {
      const map = {};
      r.data.forEach((a) => { map[a.id] = a; });
      setActivity(map);
    }).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPerm = (k, v) => setForm((f) => ({ ...f, permissions: { ...f.permissions, [k]: v } }));
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ name: s.name, email: s.email, password: "", kasa_account_id: s.kasa_account_id || "", permissions: s.permissions || empty.permissions }); setEditId(s.id); setOpen(true); };

  const submit = async () => {
    if (!form.name || (!editId && (!form.email || !form.password))) return toast.error("Ad, e-posta ve şifre gerekli");
    try {
      if (editId) {
        const payload = { name: form.name, permissions: form.permissions, kasa_account_id: form.kasa_account_id || "" };
        if (form.password) payload.password = form.password;
        await api.put(`/staff/${editId}`, payload);
      } else {
        await api.post("/staff", { ...form, kasa_account_id: form.kasa_account_id || null });
      }
      toast.success(editId ? "Personel güncellendi" : "Personel eklendi");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    }
  };

  const toggle = async (id) => { await api.post(`/staff/${id}/toggle`); load(); };
  const remove = async (id) => { await api.delete(`/staff/${id}`); toast.success("Silindi"); load(); };

  const [msgOpen, setMsgOpen] = useState(false);
  const [msgTarget, setMsgTarget] = useState({ id: null, name: "" });
  const [msgText, setMsgText] = useState("");
  const [msgPinned, setMsgPinned] = useState(false);
  const openMsg = (id, name) => { setMsgTarget({ id, name }); setMsgText(""); setMsgPinned(false); setMsgOpen(true); };
  const sendMsg = async () => {
    if (!msgText.trim()) return toast.error("Mesaj yazın");
    try {
      await api.post("/messages", { to_staff_id: msgTarget.id, text: msgText.trim(), pinned: msgPinned });
      toast.success(msgTarget.id ? `${msgTarget.name} adlı personele gönderildi` : "Tüm personele gönderildi");
      setMsgOpen(false);
      load();
    } catch (e) { toast.error("Gönderilemedi"); }
  };
  const removeMsg = async (id) => { await api.delete(`/messages/${id}`); toast.success("Sabit uyarı kaldırıldı"); load(); };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-light tracking-tight">Personel Yönetimi</h1>
          <p className="text-sm text-muted-foreground mt-1">Personel hesapları yalnızca kayıt girişi yapabilir; bakiye, rapor ve diğer verileri göremez</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => openMsg(null, "")} data-testid="broadcast-msg-btn" variant="outline" className="border-border hover:bg-secondary">
            <ChatText size={18} className="mr-1" /> Duyuru
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-staff-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus size={18} className="mr-1" /> Yeni Personel
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading">{editId ? "Personeli Düzenle" : "Yeni Personel"}</DialogTitle>
              <DialogDescription className="text-muted-foreground">Personel bu bilgilerle giriş yapıp yalnızca kayıt girebilecek.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Ad Soyad</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="staff-name" />
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">E-posta</Label>
                <Input type="email" value={form.email} disabled={!!editId} onChange={(e) => set("email", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="staff-email" />
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">{editId ? "Yeni Şifre (opsiyonel)" : "Şifre"}</Label>
                <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="staff-password" placeholder={editId ? "Değiştirmek için girin" : "En az 6 karakter"} />
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Atanmış Kasa (Hesap)</Label>
                <Select value={form.kasa_account_id || NONE} onValueChange={(v) => set("kasa_account_id", v === NONE ? "" : v)}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="staff-kasa"><SelectValue placeholder="Kasa seçin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— Kasa yok (genel) —</SelectItem>
                    {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Seçilirse personelin girdiği tüm işlemler bu kasaya işlenir (örn. Mekke Kasa)</p>
              </div>
              <div className="pt-2">
                <div className="flex items-center justify-between border border-primary/40 bg-primary/10 rounded-md px-3 py-3">
                  <div className="flex items-start gap-2 pr-3">
                    <ShieldStar size={18} weight="fill" className="text-primary mt-0.5 shrink-0" />
                    <div>
                      <div className="text-sm font-medium">Tam Yönetici Yetkisi</div>
                      <div className="text-xs text-muted-foreground">Sahip gibi tüm sisteme (bakiye, rapor, personel) erişir</div>
                    </div>
                  </div>
                  <Switch checked={!!form.permissions.admin} onCheckedChange={(v) => setPerm("admin", v)} data-testid="staff-perm-admin" />
                </div>
              </div>
              <div className="pt-2">
                <Label className="text-xs uppercase text-muted-foreground">Yetkiler — Personel neleri girebilir?</Label>
                <div className="mt-2 space-y-2">
                  {Object.keys(PERM_LABELS).map((k) => (
                    <div key={k} className={`flex items-center justify-between border border-border rounded-md px-3 py-2 bg-secondary/30 ${form.permissions.admin ? "opacity-50" : ""}`}>
                      <span className="text-sm">{PERM_LABELS[k]}</span>
                      <Switch checked={form.permissions.admin || !!form.permissions[k]} disabled={form.permissions.admin} onCheckedChange={(v) => setPerm(k, v)} data-testid={`staff-perm-${k}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} data-testid="staff-save" className="bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <p className="text-sm text-muted-foreground">Henüz personel eklenmedi.</p>}
        {items.map((s) => (
          <div key={s.id} data-testid={`staff-card-${s.id}`} className={`bg-card border rounded-lg p-6 ${s.active ? "border-border" : "border-border opacity-60"}`}>
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <UserCircle size={22} className="text-primary" />
              </div>
              <div className="flex gap-1">
                <button onClick={() => toggle(s.id)} data-testid={`staff-toggle-${s.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1" title={s.active ? "Devre dışı bırak" : "Etkinleştir"}>
                  {s.active ? <Pause size={16} /> : <Play size={16} />}
                </button>
                <button onClick={() => openMsg(s.id, s.name)} data-testid={`staff-msg-${s.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1" title="Mesaj gönder"><ChatText size={16} /></button>
                <button onClick={() => openEdit(s)} data-testid={`staff-edit-${s.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1"><PencilSimple size={16} /></button>
                <ConfirmDelete onConfirm={() => remove(s.id)} testId={`staff-delete-${s.id}`}
                  title="Personeli sil?" description={`"${s.name}" personeli silinecek. Bu işlem geri alınamaz.`}>
                  <Trash size={16} />
                </ConfirmDelete>
              </div>
            </div>
            <div className="mt-4 text-sm font-medium">{s.name}</div>
            <div className="text-xs text-muted-foreground truncate">{s.email}</div>
            {s.kasa_account_id && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-primary" data-testid={`staff-kasa-${s.id}`}>
                <Vault size={13} weight="fill" />
                Kasa: {(accounts.find((a) => a.id === s.kasa_account_id) || {}).name || "—"}
              </div>
            )}
            {messages.filter((m) => m.pinned && m.to_staff_id === s.id).map((m) => (
              <div key={m.id} data-testid={`staff-pinned-${m.id}`} className="mt-3 flex items-start gap-2 border border-primary/40 bg-primary/10 rounded-md px-2.5 py-2">
                <PushPin size={14} weight="fill" className="text-primary mt-0.5 shrink-0" />
                <span className="text-xs text-foreground flex-1 min-w-0">{m.text}</span>
                <button onClick={() => removeMsg(m.id)} data-testid={`staff-pinned-remove-${m.id}`} className="text-muted-foreground hover:text-destructive transition-colors" title="Kaldır"><Trash size={13} /></button>
              </div>
            ))}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {s.active
                ? <Badge variant="outline" className="border-[#10B981]/40 text-[#10B981] text-[10px]">Aktif</Badge>
                : <Badge variant="outline" className="border-border text-muted-foreground text-[10px]">Devre dışı</Badge>}
              {(s.permissions || {}).admin
                ? <Badge variant="outline" className="border-primary/60 text-primary text-[10px] flex items-center gap-1"><ShieldStar size={11} weight="fill" /> Yönetici</Badge>
                : Object.keys(PERM_LABELS).filter((k) => (s.permissions || {})[k]).map((k) => (
                    <Badge key={k} variant="outline" className="border-primary/30 text-primary text-[10px]">{PERM_LABELS[k]}</Badge>
                  ))}
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
              <div className="text-center">
                <div className="font-mono-num text-lg font-semibold text-primary" data-testid={`staff-today-${s.id}`}>{activity[s.id]?.today ?? 0}</div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Bugün</div>
              </div>
              <div className="text-center">
                <div className="font-mono-num text-lg font-semibold" data-testid={`staff-total-${s.id}`}>{activity[s.id]?.total ?? 0}</div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Toplam Giriş</div>
              </div>
            </div>
            {activity[s.id]?.week && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Son 7 Gün</div>
                <div className="h-16" data-testid={`staff-week-${s.id}`}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={activity[s.id].week} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                      <Tooltip cursor={{ fill: "#ffffff08" }}
                        contentStyle={{ background: "#18181B", border: "1px solid #27272A", borderRadius: 6, fontSize: 11, padding: "4px 8px" }}
                        formatter={(v) => [`${v} giriş`, ""]} labelFormatter={(l) => l} />
                      <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                        {activity[s.id].week.map((d, i) => (
                          <Cell key={i} fill={d.count > 0 ? "#D4AF37" : "#27272A"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-between text-[9px] text-muted-foreground mt-0.5">
                  {activity[s.id].week.map((d, i) => <span key={i}>{d.day.split(" ")[0]}</span>)}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading">{msgTarget.id ? `Mesaj — ${msgTarget.name}` : "Tüm Personele Duyuru"}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {msgTarget.id ? "Bu personelin panelinde bir uyarı/not gösterilir." : "Tüm personelin panelinde gösterilecek bir duyuru yazın."}
            </DialogDescription>
          </DialogHeader>
          <Textarea value={msgText} onChange={(e) => setMsgText(e.target.value)} rows={4} maxLength={500}
            className="bg-secondary border-border" data-testid="msg-text" placeholder="Kısa not / uyarı..." />
          <div className="flex items-center justify-between border border-border rounded-md px-3 py-2 bg-secondary/30">
            <div className="flex items-center gap-2">
              <PushPin size={16} className="text-primary" />
              <span className="text-sm">Sabit uyarı olarak iğnele (personel kapatamaz)</span>
            </div>
            <Switch checked={msgPinned} onCheckedChange={setMsgPinned} data-testid="msg-pinned" />
          </div>
          <DialogFooter>
            <Button onClick={sendMsg} data-testid="msg-send" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <PaperPlaneTilt size={16} className="mr-1.5" /> Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
